/**
 * VersionHistory — list of plan versions in reverse chronological
 * order. Selecting a previous version opens the DiffViewer modal that
 * consumes the existing `/api/careplans/{plan_id}/diff/{previous_id}`
 * endpoint. Diff endpoint is admin/coordinator only (server-enforced).
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, History, GitCompare } from 'lucide-react';
import { DiffViewer } from './DiffViewer';

const STATUS_TONE = {
  DRAFT: 'bg-amber-50 text-amber-700 border-amber-200',
  REVIEW: 'bg-blue-50 text-blue-700 border-blue-200',
  IN_REVIEW: 'bg-blue-50 text-blue-700 border-blue-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ARCHIVED: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

export function VersionHistory({ planId, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    api.get(`/careplans/${planId}/versions`)
      .then((r) => setVersions(r.data.data?.versions || []))
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [planId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         data-testid="structured-version-history">
      <Card className="w-[640px] max-w-full max-h-[85vh] overflow-y-auto">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <History className="h-4 w-4" /> Version history
            </h3>
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading versions…
            </div>
          ) : versions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No prior versions found.</p>
          ) : (
            <ul className="space-y-2">
              {versions.map((v) => (
                <li key={v.id} className="border rounded p-3 flex items-start gap-2"
                    data-testid={`version-row-${v.id}`}>
                  <div className="flex-1 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">v{v.version}</span>
                      <Badge variant="outline" className={STATUS_TONE[v.status] || ''}>
                        {v.status}
                      </Badge>
                      {v.id === planId && (
                        <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                          current
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">{v.id}</div>
                    {v.approved_at && (
                      <div className="text-xs text-muted-foreground">Approved {v.approved_at}</div>
                    )}
                  </div>
                  {v.id !== planId && (
                    <Button size="sm" variant="outline"
                            onClick={() => setDiff({ from: v.id, to: planId })}
                            data-testid={`version-diff-${v.id}`}>
                      <GitCompare className="h-3 w-3 mr-1" /> Diff vs current
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      {diff && (
        <DiffViewer
          planId={diff.to}
          previousId={diff.from}
          onClose={() => setDiff(null)}
        />
      )}
    </div>
  );
}

export default VersionHistory;
